/**
 * Which locale routes render their own full-viewport shell instead of the
 * normal `Header`/`Footer` (`SiteLayout.tsx`). Extracted so a second surface
 * that needs the same routing decision (`SiteNoticeBar`, `AD-SN01`) shares one
 * source of truth instead of drifting from `SiteLayout`'s own check.
 */

/**
 * Path-segment match: true only when `segment` is a whole segment of
 * `pathname`, never a substring inside a longer one (`EX-PUB11` — the old
 * `pathname.includes("/playground")` check would also fire on a hypothetical
 * `/quiz-exams/build-a-playground-lesson`).
 */
export function hasPathSegment(pathname: string, segment: string): boolean {
  return pathname.split("/").includes(segment);
}

/**
 * The round page, `/[locale]/rounds/<slug>` (`EX-DIV06`): the contest
 * browser's Judge shell, so no site header above it. Only right after the
 * locale, because the profile's `/u/<name>/rounds` is an ordinary page.
 */
export function isRoundPageRoute(pathname: string): boolean {
  return pathname.split("/").filter(Boolean)[1] === "rounds";
}

/**
 * The Judge's own shell (`EX-PUB11`), with its own bar and no site header or
 * footer: `/[locale]/judge/...`, the contest browser `/[locale]/contests/...`
 * and the round page. Only right after the locale, the round page's rule: a
 * `judge` or `contests` segment anywhere else is somebody else's page, the
 * profile's Contests tab `/u/<name>/contests` (`EX-PRF04`), which lost the
 * site bar to the old any-segment match (2026-10-21), and a person whose
 * username is `judge` or `contests`.
 */
export function isJudgeShellRoute(pathname: string): boolean {
  const first = pathname.split("/").filter(Boolean)[1];
  return first === "judge" || first === "contests" || isRoundPageRoute(pathname);
}

/**
 * `HR-ORG01b`: the Hire company workspace, `/[locale]/hire/app/...`, wears its
 * own shell (`HireShell`: the company switcher and its menu), so no site header
 * or footer. Only right after the locale; `/[locale]/hire`, the public
 * landing, keeps the site chrome.
 */
export function isHireWorkspaceRoute(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  return segments[1] === "hire" && segments[2] === "app";
}

/** Full-viewport IDE (`h-[100dvh]`, `PlaygroundLanguageSeoShell.tsx`) — an extra bar above it would clip the editor. */
export function isPlaygroundRoute(pathname: string): boolean {
  return hasPathSegment(pathname, "playground");
}

/** The quiz attempt/results shell is also fixed-viewport, and a site-wide notice has no place mid-exam. Same check `SiteLayout` already made inline. */
export function isQuizPlayerRoute(pathname: string): boolean {
  return (
    hasPathSegment(pathname, "quiz-exams") &&
    (hasPathSegment(pathname, "attempt") || hasPathSegment(pathname, "results"))
  );
}

/**
 * The Learn reader, `/[locale]/learn/[trackSlug]/[moduleKey]/[lessonKey]`
 * (`LN-RDR01`, docs/35 §5).
 *
 * It is the same shape as the judge and contest workspaces: a two-pane frame
 * with a real `PlaygroundClient` in the right pane, its own top bar carrying the
 * track and the syllabus, and a sticky pane that is the viewport minus that bar.
 * A site header above it and a footer under it would leave the editor short and
 * the footer unreachable.
 *
 * The hub (`/learn`) and the track page (`/learn/<slug>`) are ordinary scrolling
 * pages and keep the site chrome, which is why this matches the full four
 * segments after the locale rather than "anything under learn".
 *
 * `LN-TST01`: the Skill Test sitting, `/learn/<slug>/skill-test/attempt`, is the
 * same four segments and is meant to match: it wraps the quiz player, which is a
 * full-viewport exam shell. Its intro (`/skill-test`), result
 * (`/skill-test/result/<id>`) and the readiness check (`/readiness`) are
 * ordinary pages with the site chrome, and none of them is four segments.
 */
export function isLearnReaderRoute(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const learnAt = segments.indexOf("learn");
  if (learnAt === -1) return false;
  return segments.length === learnAt + 4;
}

/**
 * The practice problem workspace, `/[locale]/judge/[slug]`.
 *
 * `ProblemWorkspaceClient` is `h-[calc(100dvh-60px)]`, the viewport minus the
 * Judge topbar, with both split panes `overflow-hidden`. A footer under it
 * would be unreachable without giving a deliberately unscrollable shell a
 * scrollbar, so this is the one page in the segment that goes without one.
 *
 * The contest problem page is its own check below, because it is the same shape
 * on a different segment since `EX-PUB14`.
 *
 * The two names below are the Judge shell's own static segments rather than
 * problem slugs, and they are the same two as `RESERVED_PROBLEM_SLUGS` in the
 * backend's `Problem.model.ts`, which exists precisely so a problem slug can
 * never shadow a route. Anything else after `judge` is a problem.
 */
const JUDGE_SHELL_SEGMENTS = new Set(["problems", "leaderboard"]);

export function isJudgeWorkspaceRoute(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const judgeAt = segments.indexOf("judge");
  if (judgeAt === -1) return false;
  const next = segments[judgeAt + 1];
  return next !== undefined && !JUDGE_SHELL_SEGMENTS.has(next);
}

/**
 * The contest problem workspace, `/[locale]/contests/[slug]/problems/[letter]`.
 *
 * `EX-PUB14` moved it onto the same full-viewport split as the practice page,
 * so it grew the same footer problem: the shell is `h-[calc(100dvh-68px)]` with
 * both panes `overflow-hidden`, and a footer under that is unreachable.
 *
 * The contest page itself, its rules page and the directory are untouched: they
 * are ordinary scrolling pages and keep their footer. The match is deliberately
 * the whole `contests/<slug>/problems/<letter>` shape rather than "anything with
 * a `problems` segment", so a future `/contests/[slug]/problems` index would
 * keep one.
 */
export function isContestProblemRoute(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const contestsAt = segments.indexOf("contests");
  if (contestsAt === -1) return false;
  return segments[contestsAt + 2] === "problems" && segments[contestsAt + 3] !== undefined;
}

/** The contest browser's own pages under `/[locale]/contests/`, which are not a contest. */
const CONTEST_DIRECTORY_SEGMENTS = new Set(["host", "set-a-problem"]);

/**
 * `SUP-AI03` (`docs/38` §3 and §10.1): where the help launcher never shows.
 *
 * - A quiz or exam attempt and its result, and the Skill Test sitting (the
 *   Learn reader's shape): the chat is not mounted there, and the server
 *   refuses anyone with a timed sitting open anyway.
 * - A contest's own pages and a round's page, whatever the contest's state:
 *   the page cannot know whether its contest is running without a read, and
 *   a contestant asks there through the contest's clarifications. The
 *   directory, hosting and set-a-problem pages keep it.
 * - The full-viewport editors (the Playground, a Judge problem, the Learn
 *   reader), where a floating button would sit on the editor's own controls.
 * - An embed inside another site, an organizer's projector or print page,
 *   and the Hire company workspace, which is another product's shell.
 */
export function isHelpLauncherHiddenRoute(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[1];
  if (isQuizPlayerRoute(pathname) || isLearnReaderRoute(pathname)) return true;
  if (first === "contests" && segments[2] !== undefined && !CONTEST_DIRECTORY_SEGMENTS.has(segments[2])) return true;
  if (isRoundPageRoute(pathname) && segments[2] !== undefined) return true;
  if (isPlaygroundRoute(pathname) || (first === "judge" && isJudgeWorkspaceRoute(pathname))) return true;
  if (hasPathSegment(pathname, "embed") || isHireWorkspaceRoute(pathname)) return true;
  if (first === "dashboard" && segments[2] === "contests") {
    const page = segments[4];
    if (page === "reveal" || page === "print" || (page === "balloons" && segments[5] === "run")) return true;
  }
  return first === "dashboard" && hasPathSegment(pathname, "monitor") && segments[segments.length - 1] === "room";
}
